
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@supabase/auth-helpers-react";
import { useChurchBranding } from "@/contexts/ChurchBrandingContext";
import { recordBraceletScan } from "@/services/admin/scanService";
import { Crown } from "lucide-react";
import { useToastAction } from "@/hooks/useToastAction";

export function useBraceletCheck() {
  const [braceletId, setBraceletId] = useState<string | null>(null);
  const [isRegistered, setIsRegistered] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  const session = useSession();
  const { branding } = useChurchBranding();
  const { createToastAction } = useToastAction();

  useEffect(() => {
    checkBraceletStatus();
  }, []);

  const checkBraceletStatus = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const braceletId = urlParams.get('id') || urlParams.get('code');
    
    if (!braceletId) return;

    try {
      const cleanCode = braceletId.replace(/^GHG-?/, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      setBraceletId(cleanCode);
      console.log("Processing bracelet code:", cleanCode);
      
      // First record the scan regardless of login status or bracelet registration
      const churchCodeToUse = branding?.churchCode || 
                            (cleanCode.startsWith('TEST') ? '001' : 'GRACE');
      
      console.log(`Recording scan for bracelet ${cleanCode} with church code ${churchCodeToUse}`);
      const scanResult = await recordBraceletScan(cleanCode, churchCodeToUse, session?.user?.id);
      
      if (scanResult) {
        console.log("Scan recorded successfully for bracelet:", cleanCode);
        
        // Now check registration status - this may fail for anonymous users with non-TEST bracelets
        try {
          const { data: bracelet, error } = await supabase
            .from('bracelets')
            .select('*')
            .eq('referral_code', cleanCode)
            .maybeSingle();

          if (error && !cleanCode.startsWith('TEST')) {
            console.error("Error checking bracelet registration status:", error);
            // Only show error toast for non-TEST bracelets
            toast({
              title: "Error",
              description: "Unable to check bracelet status. Please try again later.",
              variant: "destructive",
            });
          }

          if (bracelet) {
            const isAlreadyRegistered = !!bracelet.registered_user_id;
            setIsRegistered(isAlreadyRegistered);
            
            if (isAlreadyRegistered) {
              toast({
                title: "Product Already Registered",
                description: "This bracelet has already been registered to an account.",
                variant: "destructive",
              });
            } else if (session?.user) {
              // Show register button only if user is logged in and bracelet is not registered
              setIsRegistered(false);
              
              // Show toast for logged-in users about registering the bracelet
              toast({
                title: "Register Your Bracelet",
                description: "Register this bracelet to your account to unlock all features.",
                action: createToastAction({
                  label: "Register Now",
                  onClick: () => navigate(`/products?id=${cleanCode}`),
                }),
              });
            } else if (!session?.user) {
              // Only show sign up message if no user is logged in and we're on the home page
              const churchName = branding?.churchName || (cleanCode.startsWith('TEST') ? "TEST COMM" : "Grace Awakens");
              
              // Only show this toast if on the home page, not on premium/signup pages
              if (window.location.pathname === '/' || window.location.pathname === '') {
                toast({
                  title: "Sign Up",
                  description: `Join us at ${churchName} for free to register this bracelet and access more features!`,
                  action: createToastAction({
                    label: "Sign Up",
                    onClick: () => navigate(`/premium?id=${cleanCode}`),
                    icon: <Crown className="w-4 h-4" />,
                  }),
                  duration: Infinity, // Make the toast stay until dismissed
                });
              }
            }
          } else if (cleanCode.startsWith('TEST') || session?.user) {
            // Allow registration for TEST bracelets or authenticated users
            setIsRegistered(false);
          }
        } catch (err) {
          console.error("Error checking bracelet in database:", err);
          if (!cleanCode.startsWith('TEST')) {
            toast({
              title: "Error",
              description: "There was an error checking bracelet status.",
              variant: "destructive",
            });
          }
        }
      } else {
        console.error("Failed to record scan for bracelet:", cleanCode);
        // Only show error toast for users who are authenticated, to avoid confusing new users
        if (session?.user) {
          toast({
            title: "Scan Error",
            description: "There was an error recording this scan.",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error('Error checking bracelet status:', error);
      // Only show error toast for authenticated users
      if (session?.user) {
        toast({
          title: "Error",
          description: "There was an error processing the bracelet code.",
          variant: "destructive",
        });
      }
    }
  };

  return { braceletId, isRegistered };
}
